// Solomon Portal — typed fetch wrapper for `post-conversation` Edge
// Function (issue #16 frontend half, Day 4).
//
// Contract:
//   POST /functions/v1/post-conversation
//     headers: Authorization: Bearer <jwt>, Idempotency-Key: <uuid>
//     body: { subject: string, text: string, attachmentIds?: string[] }
//
//   200 OK -> { conversationId, conversationNumber }
//   400 -> { error: 'rejected', reason: <typed> }
//   401 -> unauthorized
//   403 -> forbidden (deactivated)
//   500 -> upstream / config error
//   502 -> upstream_unavailable
//
// Spec: SPEC-reply-composer.md §2.2 + §8.

import { supabase } from './supabase';
import { env } from './env';
import { logger } from './logger';

export interface PostConversationResponse {
  conversationId: number;
  conversationNumber: number | null;
}

export type PostConversationRejectionReason =
  | 'invalid_json'
  | 'subject_required'
  | 'subject_too_short'
  | 'subject_too_long'
  | 'empty'
  | 'too_long'
  | 'too_many_attachments'
  | 'invalid_attachment_id_shape'
  | 'attachment_resolution_failed'
  | 'missing_or_invalid_idempotency_key';

export type PostConversationResult =
  | { kind: 'ok'; response: PostConversationResponse }
  | { kind: 'rejected'; reason: PostConversationRejectionReason }
  | { kind: 'unauthenticated' }
  | { kind: 'forbidden' }
  | { kind: 'upstream_unavailable' }
  | { kind: 'config_error' }
  | { kind: 'network_error' }
  | { kind: 'aborted' };

export interface PostConversationOpts {
  subject: string;
  text: string;
  attachmentIds?: string[];
  idempotencyKey: string;
  signal?: AbortSignal;
}

export async function postConversation(
  opts: PostConversationOpts,
): Promise<PostConversationResult> {
  let session;
  try {
    const { data } = await supabase.auth.getSession();
    session = data.session;
  } catch (err: unknown) {
    logger.error({
      source: 'post-conversation-api.getSession',
      message: 'getSession threw',
      payload: { error: String(err) },
    });
    return { kind: 'network_error' };
  }
  if (!session) return { kind: 'unauthenticated' };

  const body = {
    subject: opts.subject,
    text: opts.text,
    ...(opts.attachmentIds && opts.attachmentIds.length > 0
      ? { attachmentIds: opts.attachmentIds }
      : {}),
  };

  let res: Response;
  try {
    res = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/post-conversation`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        'Idempotency-Key': opts.idempotencyKey,
      },
      body: JSON.stringify(body),
      ...(opts.signal !== undefined ? { signal: opts.signal } : {}),
    });
  } catch (err: unknown) {
    if (opts.signal?.aborted) return { kind: 'aborted' };
    logger.error({
      source: 'post-conversation-api.fetch',
      message: 'fetch threw',
      payload: { error: String(err) },
    });
    return { kind: 'network_error' };
  }

  if (res.status === 401) return { kind: 'unauthenticated' };
  if (res.status === 403) return { kind: 'forbidden' };
  if (res.status === 502) return { kind: 'upstream_unavailable' };

  let parsed: unknown;
  try {
    parsed = await res.json();
  } catch {
    return { kind: 'config_error' };
  }

  if (res.status === 400) {
    const reason = (parsed as { reason?: string })?.reason;
    const validReasons: ReadonlySet<string> = new Set([
      'invalid_json',
      'subject_required',
      'subject_too_short',
      'subject_too_long',
      'empty',
      'too_long',
      'too_many_attachments',
      'invalid_attachment_id_shape',
      'attachment_resolution_failed',
      'missing_or_invalid_idempotency_key',
    ]);
    if (typeof reason === 'string' && validReasons.has(reason)) {
      return { kind: 'rejected', reason: reason as PostConversationRejectionReason };
    }
    return { kind: 'config_error' };
  }

  if (res.status !== 200) return { kind: 'config_error' };

  const obj = parsed as Partial<PostConversationResponse>;
  if (
    typeof obj.conversationId !== 'number' ||
    (obj.conversationNumber !== null && typeof obj.conversationNumber !== 'number')
  ) {
    return { kind: 'config_error' };
  }

  return {
    kind: 'ok',
    response: {
      conversationId: obj.conversationId,
      conversationNumber: obj.conversationNumber ?? null,
    },
  };
}
